/* eslint-disable tailwindcss/no-custom-classname */
/* eslint-disable lingui/no-unlocalized-strings */
import { Button, Input } from "@reactive-resume/ui";
import { cn } from "@reactive-resume/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { io, Socket } from "socket.io-client";

import { NOVAAI_PROMPTS } from "@/client/pages/builder/sidebars/left/sections/novaai/prompts";

// 消息类型定义
type Message = {
  id: string;
  content: string;
  isUser: boolean;
  timestamp: Date;
};

// 处理消息内容，将图片URL转换为Markdown图片
const processMessageContent = (content: string) => {
  // 匹配可能的图片URL (jpg, jpeg, png, gif, webp, svg)
  const imgRegex = /(https?:\/\/\S+\.(jpg|jpeg|png|gif|webp|svg)(\?\S*)?)/gi;
  return content.replace(imgRegex, "![图片]($1)");
};

// AI聊天hook
const useAICustomerService = () => {
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "1",
      content: "你好！我是Nova简历助手，有什么可以帮到你的吗？",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [isLoading, setIsLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const fullResponseRef = useRef<string>("");
  const socketRef = useRef<Socket | null>(null);
  const currentAssistantMessageIdRef = useRef<string | null>(null); // 追踪当前AI回复的ID

  // 格式化时间函数（移到外部作用域）
  const formatTime = useCallback((date: Date) => {
    return date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  }, []);

  // 初始化Socket连接
  useEffect(() => {
    // 创建WebSocket连接
    socketRef.current = io({
      path: "/api/socket.io",
      transports: ["websocket"],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1000,
      timeout: 20_000,
    });

    // 监听连接状态
    socketRef.current.on("connect", () => {
      console.log("[客服AI] WebSocket连接成功");
    });

    socketRef.current.on("disconnect", () => {
      console.log("[客服AI] WebSocket连接断开");
    });

    socketRef.current.on("connect_error", (error: Error) => {
      console.error("[客服AI] WebSocket连接错误:", error.message);
    });

    // 清理函数
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  // 发送消息到AI
  const sendMessageToAI = useCallback(
    (userContent: string) => {
      if (!userContent.trim() || isLoading) return;

      setIsLoading(true);
      setIsTyping(true);
      fullResponseRef.current = ""; // 重置累计响应内容
      currentAssistantMessageIdRef.current = null; // 重置当前AI消息ID

      // 添加用户消息
      const userMessage: Message = {
        id: Date.now().toString(),
        content: userContent,
        isUser: true,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMessage]);

      try {
        // 确保Socket已连接
        if (!socketRef.current?.connected) {
          console.log("[客服AI] Socket未连接，正在重新连接...");
          socketRef.current = io({
            path: "/api/socket.io",
            transports: ["websocket"],
            reconnection: true,
            reconnectionAttempts: 5,
            reconnectionDelay: 1000,
            timeout: 20_000,
          });
        }

        // 使用从prompts.ts导入的客服助手提示词
        const systemPrompt = NOVAAI_PROMPTS.CUSTOMER_SERVICE;

        // 构建请求选项
        const requestOptions = {
          model: "google/gemini-2.0-flash-lite-001",
          messages: [
            { role: "system", content: systemPrompt },
            // 转换之前的消息历史（最多保留5条，避免上下文太长）
            ...messages.slice(-5).map((msg) => ({
              role: msg.isUser ? ("user" as const) : ("assistant" as const),
              content: msg.content,
            })),
            // 添加当前用户消息
            { role: "user" as const, content: userContent },
          ],
          temperature: 0.5,
          top_p: 0.7,
          max_tokens: 1024,
          modelType: "nova",
        };

        console.log("[客服AI] 发送消息:", requestOptions);

        // 设置流式响应的回调
        const handleStreamContent = (content: string) => {
          console.log("[客服AI] 收到内容:", content);
          setIsTyping(false);

          // 累加新的内容片段到完整响应
          fullResponseRef.current += content;

          // 更新或创建AI回复消息
          setMessages((prev) => {
            const newMessages = [...prev];

            // 检查是否已经有当前AI消息ID
            if (currentAssistantMessageIdRef.current) {
              // 已有AI消息，更新内容
              const index = newMessages.findIndex(
                (msg) => msg.id === currentAssistantMessageIdRef.current,
              );

              if (index === -1) {
                // 如果找不到消息，创建一个新的
                const newId = `ai-${Date.now()}`;
                currentAssistantMessageIdRef.current = newId;

                newMessages.push({
                  id: newId,
                  content: fullResponseRef.current,
                  isUser: false,
                  timestamp: new Date(),
                });
              } else {
                // 更新现有AI消息内容
                newMessages[index] = {
                  ...newMessages[index],
                  content: fullResponseRef.current,
                };
              }
            } else {
              // 创建新的AI消息
              const newId = `ai-${Date.now()}`;
              currentAssistantMessageIdRef.current = newId;

              newMessages.push({
                id: newId,
                content: fullResponseRef.current,
                isUser: false,
                timestamp: new Date(),
              });
            }

            return newMessages;
          });
        };

        const handleStreamComplete = () => {
          console.log("[客服AI] 响应完成");
          setIsLoading(false);
          // 保留引用，以防最后一次更新还未完成
          setTimeout(() => {
            currentAssistantMessageIdRef.current = null;
          }, 100);
        };

        const handleStreamError = (error: string) => {
          console.error("[客服AI] 响应错误:", error);
          setIsLoading(false);
          setIsTyping(false);
          fullResponseRef.current = "";
          currentAssistantMessageIdRef.current = null;

          // 添加错误消息
          setMessages((prev) => [
            ...prev,
            {
              id: `error-${Date.now()}`,
              content: `很抱歉，发生了错误，请稍后再试。`,
              isUser: false,
              timestamp: new Date(),
            },
          ]);
        };

        // 移除可能存在的旧事件监听器
        socketRef.current.off("chat_stream_content");
        socketRef.current.off("chat_stream_done");
        socketRef.current.off("chat_stream_error");

        // 可能的替代事件名
        socketRef.current.off("stream_content");
        socketRef.current.off("stream_done");
        socketRef.current.off("stream_error");

        // 添加更多调试日志
        console.log("[客服AI] 当前消息状态:", messages);
        console.log("[客服AI] 即将注册事件监听器");

        // 监听流式响应事件 (原始事件名)
        socketRef.current.on("chat_stream_content", (content: string) => {
          console.log("[客服AI] 收到chat_stream_content事件:", content);
          handleStreamContent(content);
        });
        socketRef.current.on("chat_stream_done", () => {
          console.log("[客服AI] 收到chat_stream_done事件");
          handleStreamComplete();
        });
        socketRef.current.on("chat_stream_error", (error: string) => {
          console.log("[客服AI] 收到chat_stream_error事件:", error);
          handleStreamError(error);
        });

        // 尝试监听替代事件名
        socketRef.current.on("stream_content", (content: string) => {
          console.log("[客服AI] 收到stream_content事件:", content);
          handleStreamContent(content);
        });
        socketRef.current.on("stream_done", () => {
          console.log("[客服AI] 收到stream_done事件");
          handleStreamComplete();
        });
        socketRef.current.on("stream_error", (error: string) => {
          console.log("[客服AI] 收到stream_error事件:", error);
          handleStreamError(error);
        });

        // 发送请求并添加错误处理
        console.log("[客服AI] 发送请求到服务器");
        socketRef.current.emit("chat_stream", requestOptions, (error: Error | null) => {
          if (error) {
            console.error("[客服AI] 发送请求错误:", error);
            handleStreamError("发送请求失败");
          } else {
            console.log("[客服AI] 请求已成功发送");
          }
        });
      } catch (error) {
        console.error("[客服AI] 请求失败:", error);
        setIsLoading(false);
        setIsTyping(false);
        fullResponseRef.current = "";
        currentAssistantMessageIdRef.current = null;

        // 添加错误消息
        setMessages((prev) => [
          ...prev,
          {
            id: `error-${Date.now()}`,
            content: `很抱歉，无法连接到AI服务，请稍后再试。`,
            isUser: false,
            timestamp: new Date(),
          },
        ]);
      }
    },
    [messages, isLoading],
  );

  // 清空消息历史
  const clearMessages = useCallback(() => {
    setMessages([
      {
        id: "1",
        content: "你好！我是Nova简历助手，有什么可以帮到你的吗？",
        isUser: false,
        timestamp: new Date(),
      },
    ]);
    fullResponseRef.current = "";
    currentAssistantMessageIdRef.current = null;
  }, []);

  return {
    messages,
    isLoading,
    isTyping,
    sendMessage: sendMessageToAI,
    clearMessages,
    formatTime,
  };
};

// 主聊天组件
const CustomerServiceChat = () => {
  const { messages, isLoading, isTyping, sendMessage, formatTime } = useAICustomerService();
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // 常见问题列表
  const commonQuestions = [
    { id: 1, text: "照片上传后没显示？" },
    { id: 2, text: "导出提示ResumeNotFound？" },
    { id: 3, text: "导出提示ResumePrinterError？" },
    { id: 4, text: "订阅没有生效？" },
  ];

  // 自动滚动到最新消息
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages]);

  // 聊天窗口打开时自动聚焦输入框
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current?.focus();
      }, 100);
    }
  }, [isOpen]);

  // 切换聊天窗口显示状态
  const toggleChat = () => {
    setIsOpen((prev) => !prev);
  };

  // 处理输入变化
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(e.target.value);
  };

  // 处理按键事件
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 发送消息
  const handleSendMessage = () => {
    if (!inputValue.trim() || isLoading) return;
    sendMessage(inputValue);
    setInputValue("");
  };

  // 处理常见问题点击
  const handleQuestionClick = (question: string) => {
    if (isLoading) return;
    sendMessage(question);
  };

  // 自定义图片组件，避免在render props中使用hooks
  const CustomImageComponent = ({ src, alt }: { src?: string; alt?: string }) => {
    const [isLoaded, setIsLoaded] = useState(false);
    const [hasError, setHasError] = useState(false);

    const handleLoad = () => {
      setIsLoaded(true);
    };

    const handleError = () => {
      setHasError(true);
      setIsLoaded(false);
    };

    return (
      <div className="my-2 min-h-[100px] overflow-hidden rounded-md">
        {!isLoaded && !hasError && (
          <div className="flex h-[100px] w-full items-center justify-center bg-secondary/30">
            <div className="size-6 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
          </div>
        )}

        {hasError && (
          <div className="flex h-[100px] w-full flex-col items-center justify-center bg-secondary/20 p-2 text-center text-sm text-foreground/70">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="mb-1"
            >
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
              <line x1="12" y1="9" x2="12" y2="13"></line>
              <line x1="12" y1="17" x2="12.01" y2="17"></line>
            </svg>
            图片加载失败
            <a
              href={src}
              className="mt-1 text-xs text-primary underline"
              target="_blank"
              rel="noopener noreferrer"
            >
              查看原图链接
            </a>
          </div>
        )}

        <img
          src={src}
          alt={alt ?? "图片"}
          className={cn(
            "max-w-full rounded-md transition-opacity duration-300",
            isLoaded ? "opacity-100" : "opacity-0",
            hasError ? "hidden" : "block",
          )}
          loading="lazy"
          style={{
            objectFit: "contain",
            maxHeight: "300px",
          }}
          onLoad={handleLoad}
          onError={handleError}
        />
      </div>
    );
  };

  return (
    <div className="fixed bottom-5 right-5 z-50">
      {/* 聊天窗口 */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-80 overflow-hidden rounded-lg border border-border bg-background shadow-2xl sm:w-96">
          {/* 聊天头部 */}
          <div className="flex items-center justify-between bg-secondary p-3 text-secondary-foreground">
            <div>
              <div className="font-medium">NovaAI 智能客服</div>
              <div className="text-xs opacity-80">使用过程中的问题都可以问我哈</div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              className="text-secondary-foreground hover:bg-secondary-accent"
              onClick={toggleChat}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </Button>
          </div>

          {/* 常见问题区域 */}
          {messages.length <= 1 && (
            <div className="border-b border-border bg-secondary/10 p-3">
              <div className="mb-2 text-xs font-medium text-foreground/70">常见问题：</div>
              <div className="flex flex-wrap gap-2">
                {commonQuestions.map((q) => (
                  <Button
                    key={q.id}
                    variant="outline"
                    size="sm"
                    className="text-xs"
                    disabled={isLoading}
                    onClick={() => {
                      handleQuestionClick(q.text);
                    }}
                  >
                    {q.text}
                  </Button>
                ))}
              </div>
            </div>
          )}

          {/* 消息区域 */}
          <div className="h-96 overflow-y-auto bg-background p-4">
            {messages.length > 0 ? (
              messages.map((message) => (
                <div
                  key={message.id}
                  className={`mb-4 flex ${message.isUser ? "justify-end" : "justify-start"}`}
                >
                  <div
                    className={cn(
                      "max-w-[80%] rounded-lg p-3",
                      message.isUser
                        ? "bg-primary text-primary-foreground"
                        : "border border-border bg-background text-foreground",
                    )}
                  >
                    {message.isUser ? (
                      <div className="whitespace-pre-wrap break-words">{message.content}</div>
                    ) : (
                      <div className="markdown-body">
                        <ReactMarkdown
                          className="whitespace-pre-wrap break-words"
                          components={{
                            p: ({ children }) => <p className="mb-2">{children}</p>,
                            h1: ({ children }) => (
                              <h1 className="mb-3 mt-2 text-xl font-bold">{children}</h1>
                            ),
                            h2: ({ children }) => (
                              <h2 className="my-2 text-lg font-bold">{children}</h2>
                            ),
                            h3: ({ children }) => (
                              <h3 className="my-2 text-base font-bold">{children}</h3>
                            ),
                            ul: ({ children }) => (
                              <ul className="mb-2 ml-4 list-disc">{children}</ul>
                            ),
                            ol: ({ children }) => (
                              <ol className="mb-2 ml-4 list-decimal">{children}</ol>
                            ),
                            li: ({ children }) => <li className="mb-1">{children}</li>,
                            a: ({ href, children }) => (
                              <a
                                href={href}
                                className="text-primary hover:underline"
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                {children}
                              </a>
                            ),
                            code: ({ className, children, ...props }) => {
                              const match = /language-(\w+)/.exec(className ?? "");
                              const isInline = !className;

                              return isInline ? (
                                <code
                                  className="rounded bg-secondary px-1 py-0.5 text-sm"
                                  {...props}
                                >
                                  {children}
                                </code>
                              ) : (
                                <pre className="my-2 overflow-auto rounded bg-secondary p-2 text-sm">
                                  <code className={match ? `language-${match[1]}` : ""}>
                                    {String(children).replace(/\n$/, "")}
                                  </code>
                                </pre>
                              );
                            },
                            blockquote: ({ children }) => (
                              <blockquote className="mb-2 border-l-4 border-border pl-3 italic">
                                {children}
                              </blockquote>
                            ),
                            hr: () => <hr className="my-2 border-t border-border" />,
                            img: (props) => <CustomImageComponent {...props} />,
                            table: ({ children }) => (
                              <div className="mb-2 overflow-auto">
                                <table className="w-full border-collapse border border-border">
                                  {children}
                                </table>
                              </div>
                            ),
                            thead: ({ children }) => (
                              <thead className="bg-secondary">{children}</thead>
                            ),
                            tbody: ({ children }) => <tbody>{children}</tbody>,
                            tr: ({ children }) => (
                              <tr className="border-b border-border">{children}</tr>
                            ),
                            th: ({ children }) => (
                              <th className="border border-border p-2 text-left">{children}</th>
                            ),
                            td: ({ children }) => (
                              <td className="border border-border p-2">{children}</td>
                            ),
                          }}
                        >
                          {processMessageContent(message.content)}
                        </ReactMarkdown>
                      </div>
                    )}
                    <div
                      className={cn(
                        "mt-1 text-xs",
                        message.isUser ? "text-primary-foreground/70" : "text-foreground/50",
                      )}
                    >
                      {formatTime(message.timestamp)}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="flex h-full items-center justify-center">
                <p className="text-foreground/50">开始聊天吧...</p>
              </div>
            )}
            {isTyping && (
              <div className="mb-4 flex justify-start">
                <div className="flex items-center rounded-lg border border-border bg-background p-3 text-foreground">
                  <div className="flex space-x-1">
                    <div className="size-2 animate-bounce rounded-full bg-foreground/40"></div>
                    <div
                      className="size-2 animate-bounce rounded-full bg-foreground/40"
                      style={{ animationDelay: "0.2s" }}
                    ></div>
                    <div
                      className="size-2 animate-bounce rounded-full bg-foreground/40"
                      style={{ animationDelay: "0.4s" }}
                    ></div>
                  </div>
                  <span className="ml-2 text-xs text-foreground/50">正在输入...</span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef}></div>
          </div>

          {/* 输入区域 */}
          <div className="border-t border-border bg-background p-3">
            <div className="flex">
              <Input
                ref={inputRef}
                type="text"
                value={inputValue}
                placeholder="输入你的问题..."
                className="rounded-r-none"
                disabled={isLoading}
                onChange={handleInputChange}
                onKeyDown={handleKeyDown}
              />
              <Button
                variant={inputValue.trim() && !isLoading ? "primary" : "secondary"}
                size="icon"
                className="rounded-l-none"
                disabled={!inputValue.trim() || isLoading}
                onClick={handleSendMessage}
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <line x1="22" y1="2" x2="11" y2="13"></line>
                  <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                </svg>
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* 聊天按钮 */}
      <Button
        variant="primary"
        size="icon"
        className="flex size-12 items-center justify-center rounded-full shadow-lg"
        aria-label="聊天助手"
        onClick={toggleChat}
      >
        {isOpen ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        ) : (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="24"
            height="24"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
          </svg>
        )}
      </Button>
    </div>
  );
};

export default CustomerServiceChat;
